import useEditingStore from '@hooks/useEditingStore';
import useTemplateFeature from '@hooks/useTemplateFeature';
import { $curScene, $tourData } from '@stores/dataStores';
import {
  $GateSearchBtn,
  $editingFlag,
  $editingLangFlag,
  $editingUIColor,
} from '@stores/editingStores';
import { $isCn } from '@stores/langStores';
import { type EditConfigUI, SceneType, TemplateId } from '@types';
import { getTemplateConfig } from '@utils/templateConfig';
import { useStore } from 'effector-react';
import type { ReactNode } from 'react';
import { doubleAlpha } from '../TVip/WFeatureTogglesVip';

export function WFeatureTogglesLegacy() {
  const isMatrix = useStore($curScene)?.type === SceneType.matrix;
  const isCn = useStore($isCn);
  const langFeature = useTemplateFeature('language');
  const searchFeature = useTemplateFeature('searchSetting');
  const flag = useEditingStore({
    $data: $tourData,
    dataFn: (state) => state.flag,
    $editing: $editingFlag.store,
  });
  const langFlag = useEditingStore({
    $data: $tourData,
    dataFn: (state) => state.config?.language?.showButton,
    $editing: $editingLangFlag.store,
  });
  const showSearch = useEditingStore({
    $data: $tourData,
    dataFn: (state) => !!state.config?.search?.enabled,
    $editing: $GateSearchBtn.state,
  });
  const ui = useEditingStore({
    $data: $tourData,
    dataFn: (state) => {
      const ui = state.config?.ui;
      const defaultUI = getTemplateConfig(TemplateId.legacy)('uiColor') ?? {};
      if (!ui) return defaultUI;
      return { ...defaultUI, ...ui };
    },
    $editing: $editingUIColor.store,
  });

  const showFS = !!(flag?.showFullscreenBtn ?? 1);
  const showVR = !!(flag?.showVRBtn ?? 1) && !isMatrix;
  const showVS = !!(flag?.showViewSwitchBtn ?? 1) && !isMatrix;
  const showLang = langFeature && !!(langFlag ?? 0);
  const showHotspotBtn = !!(flag?.showHotspotBtn ?? 0);

  return (
    <div className="absolute right-15 top-15 flex flex-col">
      {showFS && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              fillRule="evenodd"
              d="M6 2H2v4h1V3h3V2Zm8 0h4v4h-1V3h-3V2Zm2 2H4v12h12V4ZM5 15V5h10v10H5Zm-3 3v-4h1v3h3v1H2Zm12-1v1h4v-4h-1v3h-3Z"
            />
          )}
        </ToggleIcon>
      )}
      {showHotspotBtn && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              d="M10 18a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-7.072-2.344a1.002 1.002 0 0 1 1.415 0 1 1 0 1 1-1.415 0Zm14.143.001a.999.999 0 1 1-1.414 1.414.999.999 0 1 1 1.414-1.414ZM10 6a4 4 0 1 1 0 8 4 4 0 0 1 0-8Zm9 3a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM1 9a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm16.071-6.071a1 1 0 1 1-1.413 1.415 1 1 0 0 1 1.413-1.415Zm-14.142 0a.999.999 0 1 1 1.414 1.414.999.999 0 1 1-1.414-1.414ZM10 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"
            />
          )}
        </ToggleIcon>
      )}
      {showVR && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              fillRule="evenodd"
              d="m13.095 16.19-2.478-3.431-.617-.854-.617.854-2.478 3.432H0V3.81h20v12.38h-6.905ZM10 10.198 6.393 15.19H1V4.81h18v10.38h-5.393L10 10.198Zm-4.762 1.232a2.381 2.381 0 1 0 0-4.762 2.381 2.381 0 0 0 0 4.762Zm11.905-2.381a2.381 2.381 0 1 1-4.762 0 2.381 2.381 0 0 1 4.762 0ZM6.214 8.07q.405.405.405.977t-.405.976q-.404.405-.976.405t-.977-.405q-.404-.404-.404-.976t.404-.977q.405-.404.977-.404t.976.404Zm9.929.977q0-.572-.405-.977-.404-.404-.976-.404t-.977.404q-.404.405-.404.977t.404.976q.405.405.977.405t.976-.405q.405-.404.405-.976Z"
            />
          )}
        </ToggleIcon>
      )}
      {showVS && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              fillRule="evenodd"
              d="M10 15.532q-3.844 0-8.381-5.337l-.265-.312.254-.322Q5.628 4.468 10 4.468q4.371 0 8.392 5.093l.254.322-.265.312Q13.844 15.532 10 15.532Zm0-1q3.274 0 7.35-4.672Q13.752 5.468 10 5.468T2.65 9.86q4.076 4.672 7.35 4.672Zm3.097-4.661a3.097 3.097 0 1 1-6.194 0 3.097 3.097 0 0 1 6.194 0Zm-1 0q0-.868-.614-1.483-.614-.614-1.483-.614-.868 0-1.483.614-.614.615-.614 1.483 0 .869.614 1.483.615.614 1.483.614.869 0 1.483-.614.614-.614.614-1.483Z"
            />
          )}
        </ToggleIcon>
      )}
      {showLang && isCn && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              d="M7.487 15.484H1.328V4.516h6.16V5.66H2.612v3.372h4.582v1.134H2.613v4.169h4.874v1.149Zm11.185 0h-1.467L11.162 6.28h-.06q.12 1.618.12 2.969v6.235h-1.184V4.516h1.451l6.024 9.168h.06q-.015-.201-.07-1.3-.05-1.099-.036-1.573V4.516h1.205v10.968Z"
            />
          )}
        </ToggleIcon>
      )}
      {showLang && !isCn && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              d="M5.864 5.665q-1.82 0-2.878 1.16Q1.933 7.984 1.933 10q0 2.077 1.013 3.21 1.018 1.13 2.903 1.13 1.155 0 2.636-.414v1.124q-1.149.434-2.832.434-2.445 0-3.77-1.432Q.557 12.621.557 9.99q0-1.648.64-2.888.64-1.245 1.845-1.916 1.204-.67 2.838-.67 1.738 0 3.039.635l-.545 1.104q-1.255-.59-2.51-.59Zm13.58 9.819h-1.468L11.933 6.28h-.06q.12 1.618.12 2.969v6.235H10.81V4.516h1.452l6.023 9.168h.06q-.015-.201-.07-1.3-.05-1.099-.036-1.573V4.516h1.205v10.968Z"
            />
          )}
        </ToggleIcon>
      )}
      {showSearch && searchFeature && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              fillRule="evenodd"
              d="M2.568 6.413Q2 7.756 2 9.226q0 1.47.568 2.813.549 1.296 1.548 2.296 1 1 2.297 1.549 1.343.568 2.813.568 1.47 0 2.813-.568 1.058-.448 1.919-1.197l3.16 3.162a.517.517 0 0 0 .73-.73l-3.16-3.161q.748-.861 1.196-1.92.568-1.342.568-2.812 0-1.47-.568-2.813-.549-1.297-1.549-2.297-1-1-2.296-1.548Q10.696 2 9.226 2q-1.47 0-2.813.568-1.297.549-2.297 1.548-1 1-1.548 2.297Zm11.274 6.943q.688-.767 1.09-1.72.487-1.15.487-2.41 0-1.26-.486-2.41-.47-1.112-1.328-1.97-.857-.857-1.969-1.327-1.15-.487-2.41-.487-1.26 0-2.41.487-1.112.47-1.97 1.327-.857.858-1.327 1.97-.487 1.15-.487 2.41 0 1.26.487 2.41.47 1.112 1.327 1.97.858.857 1.97 1.327 1.15.486 2.41.486 1.26 0 2.41-.486.953-.403 1.72-1.091a.516.516 0 0 1 .486-.486Zm-7.078-7.04a.516.516 0 0 0 .397.845h.002a.514.514 0 0 0 .396-.187q.7-.845 1.667-.845.967 0 1.667.845a.516.516 0 0 0 .397.187h.005a.516.516 0 0 0 .511-.516V6.64a.516.516 0 0 0-.118-.324q-1.01-1.22-2.462-1.22-1.452 0-2.462 1.22Z"
            />
          )}
        </ToggleIcon>
      )}
    </div>
  );
}

function ToggleIcon({
  ui,
  children,
}: { ui: EditConfigUI; children: (props: { color: string }) => ReactNode }) {
  return (
    <div
      className="mb-10 h-32 w-32 rounded-full flex items-center justify-center"
      style={{
        backgroundColor: `rgba(${ui.bgColor})`,
        border: `1px solid rgba(${doubleAlpha(ui.bgColor)})`,
      }}
    >
      <svg width={20} height={20} xmlnsXlink="http://www.w3.org/1999/xlink">
        {children({ color: `rgba(${ui.fontColor})` })}
      </svg>
    </div>
  );
}
